<template>
  <dx-popup
    :visible.sync="isVisible"
    :drag-enabled="false"
    :close-on-outside-click="true"
    :show-title="true"
    :title="$t('directory')"
  >
    <dx-scroll-view>
      <dx-data-grid
        ref="consigmentGrid"
        :data-source="consigmentInfoStore"
        :allow-column-resizing="true"
        :allow-column-reordering="true"
        class="mb-3"
        @selectionChanged="updateSelection"
      >
        <dx-column-chooser :enabled="true" />

        <dx-column
          v-if="user.can_select_company"
          data-field="company"
          ок
          :caption="$t('company')"
          :allow-search="false"
        >
          <dx-lookup
            :data-source="companies"
            value-expr="id"
            :display-expr="companyDisplayExpr"
          />
        </dx-column>
        <dx-column
          v-if="user.is_manager"
          data-field="public"
          :caption="$t('public')"
          :allow-search="false"
          data-type="boolean"
        />

        <dx-column
          v-for="it in consigmentFields"
          :key="it.key"
          :data-field="it.key"
          :caption="it.label"
          :max-length="it.maxLength"
          :allow-search="true"
          :width="it.width"
        >
          <dx-lookup
            v-if="it.key === 'countrycode'"
            :data-source="countries"
            value-expr="abc2"
            :display-expr="countryDisplayExpr"
          />
        </dx-column>
        <dx-selection mode="single" />
        <dx-editing
          :allow-updating="allowEditing"
          :allow-adding="true"
          :allow-deleting="allowEditing"
          :use-icons="true"
          mode="form"
        />

        <dx-paging :page-size="8" />

        <dx-pager
          :show-page-size-selector="false"
          :allowed-page-sizes="[8]"
        />

        <dx-state-storing
          :enabled="true"
          type="localStorage"
          :storage-key="`u${user.id}-${mode}-company-selector`"
        />

        <dx-search-panel
          :visible="true"
          :highlight-case-sensitive="false"
        />
      </dx-data-grid>
      <b-row class="ml-0 mr-1">
        <dx-button
          :text="$t('select')"
          :disabled="!selectedRow"
          type="success"
          styling-mode="contained"
          class="custom-btn mr-2 ml-auto"
          @click="updateConsigmentInfo"
        />
        <dx-button
          :text="$t('Cancel')"
          type="success"
          styling-mode="text"
          class="custom-btn"
          @click="isVisible = false"
        />
      </b-row>
    </dx-scroll-view>
  </dx-popup>
</template>

<script>
import { mapState } from 'vuex';

import { DxButton } from 'devextreme-vue';
import { DxPopup } from 'devextreme-vue/popup';
import { DxScrollView } from 'devextreme-vue/scroll-view';

import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxSelection,
  DxLookup,
  DxPaging,
  DxPager,
  DxStateStoring,
  DxSearchPanel,
  DxColumnChooser,
} from 'devextreme-vue/data-grid';

import createCustomStore from '@/utils/create-custom-store';

export default {
  name: 'ConsigmentInfoModal',
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxSelection,
    DxPaging,
    DxPager,
    DxButton,
    DxPopup,
    DxScrollView,
    DxLookup,
    DxStateStoring,
    DxSearchPanel,
    DxColumnChooser,
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
    countries: {
      type: Object,
    },
    countryDisplayExpr: {
      type: Function,
    },
    companies: {
      type: Object,
    },
    companyDisplayExpr: {
      type: Function,
    },
  },
  data: () => ({
    consigmentInfoStore: createCustomStore('/tracking/api/directory/company/'),
    selectedRow: null,
  }),
  computed: {
    ...mapState(['user']),
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    consigmentFields() {
      return [
        {
          key: 'short_name',
          label: this.$t('title'),
          width: 300,
        },
        {
          key: 'inn',
          label: this.$t('INN'),
          maxLength: 12,
        },
        {
          key: 'okpo',
          label: this.$t('OKPO'),
        },
        {
          key: 'rail_code',
          label: this.$t('Railway code'),
        },
        {
          key: 'countrycode',
          label: this.$t('Country'),
          width: 150,
        },
        {
          key: 'postalcode',
          label: this.$t('Postal code'),
          maxLength: 9,
        },
        {
          key: 'city',
          label: this.$t('City'),
          maxLength: 35,
        },
        {
          key: 'streethouse',
          label: this.$t('Address'),
          maxLength: 50,
        },
        {
          key: 'contact_person',
          label: this.$t('Contact person'),
          maxLength: 100,
        },
        {
          key: 'phone',
          label: this.$t('Phone'),
          maxLength: 100,
        },
        {
          key: 'email',
          label: this.$t('Email'),
          maxLength: 50,
        },
      ];
    },
    consigmentInfo() {
      if (!this.selectedRow) {
        return null;
      }

      return this.consigmentFields.reduce((acc, it) => {
        if (it.key === 'short_name') {
          acc[this.mode] = this.selectedRow[it.key];
          return acc;
        }

        acc[`${this.mode}_${it.key}`] = this.selectedRow[it.key];
        return acc;
      }, {});
    },
  },
  methods: {
    updateConsigmentInfo() {
      this.$emit('update', this.consigmentInfo);
      this.isVisible = false;
    },
    updateSelection({ selectedRowsData }) {
      this.selectedRow = selectedRowsData[0];
    },
    allowEditing(e) {
      return !e.row.data.public || this.user.is_manager;
    },
  },
};
</script>

<style>
</style>
